import {openConfirmDialog, openDialog, Switch} from 'platform/components';
import {Box, Heading, HStack, Icon, Show, Space, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {
  useDeactivateMultiFactorAuthenticationMutation,
  useDeactivateUserMultiFactorAuthenticationMutationMutation,
  useGetMultiFactorAuthenticationSettingsQuery,
  useResetUserMultiFactorAuthenticationMutationMutation,
} from '@dms/api/base';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import type {ActionCallback} from 'features/eag-grid';

import {GracePeriodForm} from './components/GracePeriodForm';

export function TwoFactorSettings() {
  const {data: mfaSettings, isLoading, isError} = useGetMultiFactorAuthenticationSettingsQuery();
  const [deactivateMFA] = useDeactivateMultiFactorAuthenticationMutation();
  const [deactivateUserMFA] = useDeactivateUserMultiFactorAuthenticationMutationMutation();
  const [resetUserMFA] = useResetUserMultiFactorAuthenticationMutationMutation();

  const isTwoFactorEnabled = mfaSettings?.active ?? false;

  const handleOnChange = (shouldEnableMFA: boolean) => {
    if (shouldEnableMFA) {
      return openDialog(<GracePeriodForm />, {
        title: i18n.t('page.settings.labels.gracePeriod'),
        size: 'small',
      });
    }

    openConfirmDialog({
      text: i18n.t('page.settings.labels.deactivateMFAConfirmation'),
      onConfirm: () => deactivateMFA().unwrap().catch(handleApiError),
    });
  };

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('deactivate', () =>
        deactivateUserMFA({userId: id})
          .unwrap()
          .then(() => {
            refreshData();
          })
          .catch(handleApiError)
      )
      .with('reset', () =>
        resetUserMFA({userId: id})
          .unwrap()
          .then(() => {
            refreshData();
          })
          .catch(handleApiError)
      );
  };

  return (
    <SettingsTemplate
      header={{title: i18n.t('page.settings.labels.multiFactorAuth')}}
      data-testid={testIds.settings.twoFactorSettings('settings-template')}
      isLoading={isLoading}
      isError={isError}
    >
      <VStack>
        <SettingsSection>
          <VStack spacing={6}>
            <Box backgroundColor="palettes.blue.10.100" padding={4} borderRadius="medium">
              <HStack spacing={4} align="center">
                <Icon size={20} value="hardware/shield" isNotFilled />

                <VStack spacing={2}>
                  <Heading size={4}>{i18n.t('page.settings.labels.multiFactorAuth')}</Heading>
                  <Text size="small" color="secondary">
                    {i18n.t('page.settings.labels.multiFactorAuthDescription')}
                  </Text>
                </VStack>
              </HStack>
            </Box>

            <Switch
              value={isTwoFactorEnabled}
              onChange={handleOnChange}
              data-testid={testIds.settings.twoFactorSettings('switch')}
              label={i18n.t('page.settings.labels.multiFactorAuth')}
            />
          </VStack>
        </SettingsSection>

        <Show when={isTwoFactorEnabled}>
          <Space vertical={6} />

          <Box height={150}>
            <DmsDataGrid
              gridCode="user-multi-factor-authentication"
              actionCallback={actionCallback}
            />
          </Box>
        </Show>
      </VStack>
    </SettingsTemplate>
  );
}
